import type { ActionType, ProTableProps } from "@ant-design/pro-components";
import { ProTable } from "@ant-design/pro-components";
import type { AlertProps } from "antd";
import { Alert, Space } from "antd";
import type { ReactNode } from "react";
import { Fragment } from "react";
import type { AdminPageActionItem } from "./admin-action-types";
import { AdminLegendGroups } from "./admin-legend-components";
import type { AdminLegendGroup } from "./admin-legend-types";
import { AdminListToolbarActions } from "./admin-page-actions";
import { AdminPageContainer, type SharedAdminPageContainerProps } from "./admin-page-container";
import { AdminListScaffold, buildAdminProTableCommonProps } from "./admin-table";

export {
  ADMIN_MUTED_SURFACE_STYLE,
  ADMIN_SURFACE_BORDER_STYLE,
  ADMIN_TERTIARY_TEXT_STYLE,
  AdminPageContainer,
} from "./admin-page-container";

export type AdminPageNoticeItem = Omit<AlertProps, "message"> & {
  key: string;
  message?: ReactNode;
};

export function AdminPageNotice({ notices }: { notices: AdminPageNoticeItem[] }) {
  const visibleNotices = notices.filter(Boolean);
  if (visibleNotices.length === 0) {
    return null;
  }
  return (
    <Space vertical size={12} style={{ display: "flex" }}>
      {visibleNotices.map((item) => {
        const { key, message, title, ...props } = item;
        return <Alert key={key} {...props} title={title ?? message} />;
      })}
    </Space>
  );
}

export type AdminSectionWorkspaceProps = SharedAdminPageContainerProps & {
  notices?: AdminPageNoticeItem[];
  legendGroups?: AdminLegendGroup[];
  content: ReactNode;
  contentSpacing?: number;
  children?: ReactNode;
};

export function AdminSectionWorkspace({
  notices = [],
  legendGroups = [],
  content,
  contentSpacing = 16,
  children,
  ...containerProps
}: AdminSectionWorkspaceProps) {
  return (
    <AdminPageContainer {...containerProps}>
      <Space vertical size={contentSpacing} style={{ display: "flex" }}>
        <AdminPageNotice notices={notices} />
        <AdminLegendGroups groups={legendGroups} />
        {content}
      </Space>
      {children}
    </AdminPageContainer>
  );
}

type AdminSectionTablePanelProps<TData extends Record<string, unknown>> = {
  rowKey: string;
  loading?: boolean;
  columns: NonNullable<ProTableProps<TData, Record<string, unknown>>["columns"]>;
  dataSource?: TData[];
  request?: ProTableProps<TData, Record<string, unknown>>["request"];
  pagination?: ProTableProps<TData, Record<string, unknown>>["pagination"];
  expandable?: ProTableProps<TData, Record<string, unknown>>["expandable"];
  tableLayout?: ProTableProps<TData, Record<string, unknown>>["tableLayout"];
  scroll?: ProTableProps<TData, Record<string, unknown>>["scroll"];
  headerActions?: AdminPageActionItem[];
  onReload?: (action?: ActionType | null) => void | Promise<void>;
  persistenceKey: string;
  search?: ProTableProps<TData, Record<string, unknown>>["search"];
};

export function AdminSectionTablePanel<TData extends Record<string, unknown>>({
  rowKey,
  loading,
  columns,
  dataSource,
  request,
  pagination = false,
  expandable,
  tableLayout,
  scroll,
  headerActions = [],
  onReload,
  persistenceKey,
  search = false,
}: AdminSectionTablePanelProps<TData>) {
  const optionalTableProps: Partial<ProTableProps<TData, Record<string, unknown>>> = {
    ...(loading !== undefined ? { loading } : {}),
    ...(dataSource !== undefined ? { dataSource } : {}),
    ...(request !== undefined ? { request } : {}),
    ...(expandable !== undefined ? { expandable } : {}),
    ...(tableLayout !== undefined ? { tableLayout } : {}),
    ...(scroll !== undefined ? { scroll } : {}),
  };

  return (
    <AdminListScaffold
      table={
        <ProTable<TData>
          rowKey={rowKey}
          search={search}
          cardBordered
          columns={columns}
          pagination={pagination}
          {...optionalTableProps}
          toolBarRender={
            headerActions.length > 0
              ? () => [
                  <Fragment key="table-actions">
                    <AdminListToolbarActions actions={headerActions} />
                  </Fragment>,
                ]
              : () => []
          }
          tableAlertRender={false}
          tableAlertOptionRender={false}
          {...buildAdminProTableCommonProps<TData>({
            persistenceKey,
            ...(onReload ? { reload: onReload } : {}),
          })}
        />
      }
    />
  );
}
